/**
 * Maximum-spending solve (spec §7): binary-search the constant real spending
 * level that exhausts the portfolio exactly at the horizon. Answers "what can
 * I actually afford?" — the question people most want answered.
 *
 * (The Monte Carlo success-probability variant arrives with phase 12; this is
 * the deterministic solve under the active assumption profile.)
 */
import type { Plan } from '../model/types';
import { runProjection } from './projection';
import { CURRENT_YEAR } from '../model/defaults';

export interface MaxSpendingResult {
  /** Constant annual spending in today's dollars that the plan just sustains. */
  annualSpendingToday: number;
  /** Current budget's year-one spending, for comparison. */
  iterations: number;
}

/** True when the plan survives to the horizon with this spending level. */
function sustains(base: Plan, spending: number): boolean {
  const variant: Plan = structuredClone(base);
  variant.budget = {
    mode: 'simple',
    annualAmount: spending,
    retirementSpendingMultiplier: 1,
    essentialFraction: variant.budget.essentialFraction ?? 0.65,
    ageBands: variant.budget.ageBands,
  };
  variant.decisions = { ...variant.decisions, withdrawalStrategy: 'fund_spending' };
  const r = runProjection(variant, CURRENT_YEAR);
  return r.firstDepletionYear === undefined;
}

export function solveMaxSpending(plan: Plan): MaxSpendingResult {
  let iterations = 0;

  // Bracket the boundary: grow `hi` until it breaks the plan (cap $5M/yr).
  let lo = 0;
  let hi = 100_000;
  while (sustains(plan, hi) && hi < 5_000_000) {
    lo = hi;
    hi *= 2;
    iterations++;
  }

  // Binary search to <$250 resolution.
  while (hi - lo > 250 && iterations < 48) {
    const mid = (lo + hi) / 2;
    if (sustains(plan, mid)) lo = mid;
    else hi = mid;
    iterations++;
  }

  return { annualSpendingToday: Math.floor(lo / 100) * 100, iterations };
}
